import { useEffect, useState } from "react";
import { FileText, ScanLine, LayoutTemplate, Trash2, Star, Pencil, Folder, Palette, Scale, Receipt, Mail, BarChart3, ClipboardList, Briefcase, Heart, Home, GraduationCap, FileSignature, Users, Lightbulb } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { folderMeta, setFolderMeta } from "../../lib/storage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { TEMPLATES } from "../../lib/templates";
import { renderPages } from "../../lib/render";
import { dims, defaultSettings } from "../../lib/pages";

export const FOLDER_ICONS = { folder: Folder, scale: Scale, receipt: Receipt, mail: Mail, chart: BarChart3, clipboard: ClipboardList, briefcase: Briefcase, signature: FileSignature, users: Users, star: Star, heart: Heart, home: Home, school: GraduationCap, idea: Lightbulb };
const FOLDER_COLORS = ["#0F6CBD", "#7C3AED", "#059669", "#C2410C", "#BE185D", "#0E7490", "#CA8A04", "#64748B"];
const slug = (n) => n.toLowerCase().replace(/\s+/g, "-");

const FolderStyler = ({ name, onChange }) => {
  const m = folderMeta(name);
  const set = (p) => { setFolderMeta(name, { ...m, ...p }); onChange(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="h-7 w-7 rounded-full border bg-card hover:bg-accent flex items-center justify-center" title={`Colour & icon for ${name}`} data-testid={`folder-style-${slug(name)}`}><Palette className="h-3.5 w-3.5 text-muted-foreground" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 space-y-3" data-testid={`folder-style-popover-${slug(name)}`}>
        <div className="text-xs font-semibold">{name}</div>
        <div className="flex flex-wrap gap-1.5">
          {FOLDER_COLORS.map((c) => (
            <button key={c} type="button" onClick={() => set({ color: c })} data-testid={`folder-color-${c.slice(1)}`} aria-label={`Colour ${c}`}
              className={`h-6 w-6 rounded-full border-2 ${m.color === c ? "border-foreground" : "border-transparent"}`} style={{ background: c }} />
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Object.entries(FOLDER_ICONS).map(([k, I]) => (
            <button key={k} type="button" onClick={() => set({ icon: k })} data-testid={`folder-icon-${k}`} aria-label={`Icon ${k}`}
              className={`h-8 w-8 rounded-md border flex items-center justify-center ${m.icon === k ? "border-primary bg-primary/10" : "hover:bg-accent"}`}><I className="h-4 w-4" style={{ color: m.color }} /></button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Thumb = ({ t }) => {
  const [html, setHtml] = useState("");
  const s = { ...defaultSettings(), ...(t.settings || {}) };
  useEffect(() => { const id = setTimeout(() => setHtml(renderPages(t.html, { ...defaultSettings(), ...(t.settings || {}) })[0]), 50); return () => clearTimeout(id); }, [t]);
  const d = dims(s);
  const k = 150 / d.w;
  return (
    <div className="h-[212px] w-[150px] overflow-hidden rounded-md border bg-white shadow-sm group-hover:shadow-md transition-shadow pointer-events-none">
      <div style={{ zoom: k }} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

const Card = ({ t, onPick, onDelete, onEdit, testid, color }) => (
  <div className="group relative shrink-0">
    <button type="button" onClick={() => onPick(t)} className="text-left focus:outline-none" data-testid={testid} title={t.desc}>
      <div className="rounded-lg p-1 group-hover:ring-2 group-hover:ring-primary/40 transition" style={color ? { boxShadow: `inset 0 3px 0 ${color}` } : undefined}><Thumb t={t} /></div>
      <div className="mt-1.5 px-1 text-sm font-medium w-[158px] truncate">{t.name}</div>
      <div className="px-1 text-[11px] text-muted-foreground w-[158px] truncate">{t.desc || "My template"}</div>
    </button>
    {onEdit && (
      <button type="button" onClick={() => onEdit(t)} data-testid={`${testid}-edit`} aria-label={`Edit template ${t.name}`}
        className="absolute top-2 right-10 h-7 w-7 rounded-md bg-white/90 border text-slate-500 hover:text-primary opacity-0 group-hover:opacity-100 transition flex items-center justify-center"><Pencil className="h-3.5 w-3.5" /></button>
    )}
    {onDelete && (
      <button type="button" onClick={() => onDelete(t)} data-testid={`${testid}-delete`} aria-label={`Delete template ${t.name}`}
        className="absolute top-2 right-2 h-7 w-7 rounded-md bg-white/90 border text-slate-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
    )}
  </div>
);

const MyTemplates = ({ mine, onPick, onDelete, onEdit }) => {
  const [folder, setFolder] = useState("All");
  const [, repaint] = useState(0);
  const groups = {};
  mine.forEach((t, i) => { const c = t.category || "General"; (groups[c] = groups[c] || []).push([t, i]); });
  const names = Object.keys(groups).sort();
  const active = folder === "All" || groups[folder] ? folder : "All";
  const shown = active === "All" ? names : names.filter((n) => n === active);
  return (
    <div className="mb-5" data-testid="my-templates">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Star className="h-4 w-4 text-amber-500" /><h2 className="text-base font-semibold">My templates</h2>
        <span className="text-xs text-muted-foreground mr-2">({mine.length})</span>
        {["All", ...names].map((n) => {
          const m = folderMeta(n);
          const I = FOLDER_ICONS[m.icon] || Folder;
          return (
            <button key={n} type="button" onClick={() => setFolder(n)} data-testid={`folder-chip-${slug(n)}`}
              className={`h-7 px-2.5 rounded-full border text-xs flex items-center gap-1 transition-colors ${active === n ? "bg-primary/10 text-foreground" : "bg-card hover:bg-accent"}`}
              style={n !== "All" ? { borderColor: active === n ? m.color : undefined } : undefined}>
              {n !== "All" && <I className="h-3.5 w-3.5" style={{ color: m.color }} />}{n} <span className="opacity-60">{n === "All" ? mine.length : groups[n].length}</span>
            </button>
          );
        })}
      </div>
      <div className="space-y-4">
        {shown.map((n) => (
          <div key={n} data-testid={`template-folder-group-${n.toLowerCase().replace(/\s+/g, "-")}`}>
            {(() => { const m = folderMeta(n); const I = FOLDER_ICONS[m.icon] || Folder; return (
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: m.color }} data-testid={`folder-header-${slug(n)}`}>
                <span className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: `${m.color}1A` }}><I className="h-3.5 w-3.5" /></span>{n}
                <FolderStyler name={n} onChange={() => repaint((x) => x + 1)} />
              </div>
            ); })()}
            <div className="flex gap-4 overflow-x-auto pb-2">
              {groups[n].map(([t, i]) => <Card key={t.id} t={t} onPick={onPick} onDelete={onDelete} onEdit={onEdit} testid={`my-template-${i}`} color={folderMeta(n).color} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TemplateStrip = ({ onPick, mine = [], onDeleteMine, onEditMine }) => (
  <section className="mb-7" data-testid="template-strip">
    {mine.length > 0 && <MyTemplates mine={mine} onPick={onPick} onDelete={onDeleteMine} onEdit={onEditMine} />}
    <div className="flex items-center gap-2 mb-3">
      <LayoutTemplate className="h-4 w-4 text-primary" />
      <h2 className="text-base font-semibold">Start from a template</h2>
    </div>
    <div className="flex gap-4 overflow-x-auto pb-2">
      {TEMPLATES.map((t) => <Card key={t.id} t={t} onPick={onPick} testid={`template-${t.id}`} />)}
    </div>
  </section>
);

export const PdfModeDialog = ({ file, onChoose, onClose }) => (
  <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-lg" data-testid="pdf-mode-dialog">
      <DialogHeader>
        <DialogTitle>How should “{file?.name}” open?</DialogTitle>
        <DialogDescription>Pick editable text to rework the content, or exact pages to fill in and sign the PDF as it looks.</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3">
        <button type="button" onClick={() => onChoose("pages")} data-testid="pdf-mode-pages" className="flex gap-3 rounded-lg border p-4 text-left hover:border-primary hover:bg-primary/5 transition-colors">
          <ScanLine className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <span><span className="block font-medium">Exact pages — edit & sign</span><span className="text-sm text-muted-foreground">Each page looks identical to the original. Click a page to white-out content, add new text, dates, initials or signatures on top.</span></span>
        </button>
        <button type="button" onClick={() => onChoose("text")} data-testid="pdf-mode-text" className="flex gap-3 rounded-lg border p-4 text-left hover:border-primary hover:bg-primary/5 transition-colors">
          <FileText className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <span><span className="block font-medium">Editable text</span><span className="text-sm text-muted-foreground">Extracts the text into paragraphs you can edit. Layout and images may be lost.</span></span>
        </button>
      </div>
    </DialogContent>
  </Dialog>
);
